import { z } from 'zod';
import { isAxiosError } from 'axios';
import { posClient } from './client';
import {
  normalizeOrder,
  wireActiveOrdersSchema,
  wireFbrRetrySchema,
  wireOrderDetailsSchema,
  wireOrdersIndexSchema,
  wireStatusUpdateSchema,
  type ActiveOrders,
  type Order,
} from '@/types/orders';
import {
  normalizeAppliedEdit,
  wireOrderEditConflictSchema,
  wireOrderEditResponseSchema,
  type AppliedOrderEdit,
  type OrderEditPayload,
} from '@/types/order-edit';
import type { Paginated } from '@/types/common';
import {
  emailDeliveryListResponseSchema,
  emailDeliveryResponseSchema,
  type OrderEmailDelivery,
} from '@/types/order-email-deliveries';

/**
 * Order read/status adapters (V1-05). Endpoint evidence in docs/05a-api-baseline.md
 * §10.2 (pos-api OrderController). Order reads accept an Office manager without
 * a terminal; register requests still send a terminal and remain terminal-scoped.
 *
 * Order CREATE belongs to V1-04 (src/api/checkout.ts) — not here.
 */

export interface OrderRequestOptions {
  signal?: AbortSignal;
}

export interface OrderListParams {
  /** Exact customer identity; combined with the existing history filters. */
  customer_id?: number;
  /** Matches name, phone, email, order_number, or id (LIKE). */
  search?: string;
  /** A wire status or 'all'. Values outside the seven POS statuses 422. */
  status?: string;
  /** NOTE: camelCase on the wire — `orderType`, values delivery|pickup|dinein|all. */
  orderType?: string;
  date_from?: string;
  date_to?: string;
  min_amount?: number;
  max_amount?: number;
  per_page?: number;
  page?: number;
}

export interface UpdateStatusOptions extends OrderRequestOptions {
  /** Appended to the order's existing notes upstream (never replaced). */
  notes?: string;
  /**
   * Adopts a website order into this day session — applied by the upstream
   * only when the order has NO session yet (Internal/OrderController.php:141-144).
   */
  daySessionId?: number;
}

/**
 * Another device saved first (409 `ORDER_EDIT_CONFLICT`).
 *
 * Its own type because the operator's next action is unlike every other
 * failure: their draft is INTACT and must be kept, and what they need is to see
 * what changed underneath them. The fresh order rides along for exactly that.
 */
export class OrderEditConflictError extends Error {
  constructor(
    readonly freshOrder: Order | null,
    readonly currentRevision: number | null,
    message: string,
  ) {
    super(message);
    this.name = 'OrderEditConflictError';
  }
}

/**
 * The caller may not make THIS edit (403 `ORDER_EDIT_FORBIDDEN`).
 *
 * `capability` names the grant that was missing, so the editor can say "a
 * manager has to save this" rather than "forbidden" — the operator's next step
 * is to fetch someone, and a generic refusal does not tell them that.
 */
export class OrderEditForbiddenError extends Error {
  constructor(
    readonly capability: string | null,
    message: string,
  ) {
    super(message);
    this.name = 'OrderEditForbiddenError';
  }
}

/**
 * The edit key was already spent on a different change (409).
 *
 * Retrying under the same key cannot help. The editor reloads the order and
 * starts a fresh operation rather than looping on a key the server has already
 * bound to something else.
 */
export class OrderEditOperationConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'OrderEditOperationConflictError';
  }
}

/**
 * Editing runs through the same 30s upstream proxy the create does, so the
 * local budget is deliberately longer: the server should settle first whenever
 * it can, and a local timeout is the one outcome where the console does not
 * know what happened. `client_edit_id` is what makes the retry after that safe.
 */
export const ORDER_EDIT_TIMEOUT_MS = 45_000;

async function fetchOrderHistory(
  params: OrderListParams,
  options: OrderRequestOptions,
  skipTerminalHeader: boolean,
): Promise<Paginated<Order>> {
  const res = await posClient.get('/orders', {
    params,
    signal: options.signal,
    skipTerminalHeader,
  });
  const body = wireOrdersIndexSchema.parse(res.data);
  return {
    items: body.data.map(normalizeOrder),
    page: body.current_page,
    lastPage: body.last_page,
    perPage: Number(body.per_page),
    total: body.total,
  };
}

export const ordersApi = {
  async getTags(orderId: number, options: OrderRequestOptions = {}): Promise<string[]> {
    const { data } = await posClient.get(`/orders/${orderId}/tags`, {
      signal: options.signal,
      skipTerminalHeader: true,
    });
    return z.object({ data: z.object({ tags: z.array(z.string()) }) }).parse(data).data.tags;
  },

  async updateTags(
    orderId: number,
    changes: { add?: string[]; remove?: string[] },
  ): Promise<string[]> {
    const { data } = await posClient.patch(`/orders/${orderId}/tags`, changes, {
      skipTerminalHeader: true,
    });
    return z.object({ data: z.object({ tags: z.array(z.string()) }) }).parse(data).data.tags;
  },

  /**
   * GET {pos}/orders — organization-wide office history, newest first.
   * Register queue reads use getNew() and keep their selected-terminal scope.
   */
  async list(
    params: OrderListParams = {},
    options: OrderRequestOptions = {},
  ): Promise<Paginated<Order>> {
    return fetchOrderHistory(params, options, true);
  },

  /** Register history retains X-Terminal-Id, assignment and branch checks. */
  async listForRegister(
    params: OrderListParams = {},
    options: OrderRequestOptions = {},
  ): Promise<Paginated<Order>> {
    return fetchOrderHistory(params, options, false);
  },

  /**
   * GET {pos}/orders/new — the active board. Excludes cancelled + delivered.
   * With `day_session_id` it returns that session's orders PLUS unassigned
   * pending (website) orders (OrderController.php:352-360).
   */
  async getNew(
    daySessionId?: number | null,
    options: OrderRequestOptions = {},
  ): Promise<ActiveOrders> {
    const res = await posClient.get('/orders/new', {
      params: daySessionId != null ? { day_session_id: daySessionId } : {},
      signal: options.signal,
    });
    const body = wireActiveOrdersSchema.parse(res.data);
    return {
      pending: body.data.pending.map(normalizeOrder),
      inprogress: body.data.inprogress.map(normalizeOrder),
    };
  },

  /**
   * GET {pos}/orders/{id} — 404 is the nested envelope
   * `{ success:false, error:{ code:'ORDER_NOT_FOUND', message } }`;
   * normalizeApiError surfaces it as { status:404, code:'ORDER_NOT_FOUND' }.
   */
  async getDetails(orderId: number, options: OrderRequestOptions = {}): Promise<Order> {
    const res = await posClient.get(`/orders/${orderId}`, { signal: options.signal });
    return normalizeOrder(wireOrderDetailsSchema.parse(res.data).data);
  },

  async getEmailDeliveries(
    orderId: number,
    options: OrderRequestOptions = {},
  ): Promise<OrderEmailDelivery[]> {
    const res = await posClient.get(`/orders/${orderId}/email-deliveries`, {
      signal: options.signal,
    });
    return emailDeliveryListResponseSchema.parse(res.data).data;
  },

  async resendConfirmationEmail(orderId: number): Promise<OrderEmailDelivery> {
    const res = await posClient.post(`/orders/${orderId}/email-deliveries/resend`);
    return emailDeliveryResponseSchema.parse(res.data).data;
  },

  /** GET the private receipt through the authenticated tenant-scoped endpoint. */
  async getPaymentReceipt(orderId: number, options: OrderRequestOptions = {}): Promise<Blob> {
    const res = await posClient.get(`/orders/${orderId}/payment-receipt`, {
      signal: options.signal,
      responseType: 'blob',
    });
    return res.data as Blob;
  },

  /**
   * PUT {pos}/orders/{id}/status — 200 `{ success, message }` with NO order
   * body. Callers MUST re-fetch the queue/detail after success; this module
   * never fabricates the resulting order state.
   *
   * The backend checks the business-specific transition policy and returns
   * 409 when the current order no longer permits the requested transition.
   */
  async updateStatus(
    orderId: number,
    status: string,
    options: UpdateStatusOptions = {},
  ): Promise<void> {
    const body: Record<string, unknown> = { status };
    if (options.notes !== undefined && options.notes !== '') body.notes = options.notes;
    if (options.daySessionId != null) body.day_session_id = options.daySessionId;
    const res = await posClient.put(`/orders/${orderId}/status`, body, {
      signal: options.signal,
    });
    wireStatusUpdateSchema.parse(res.data);
  },

  /**
   * Re-attempt a definitively rejected FBR submission. The backend refuses
   * submitted, in-flight, and ambiguous outcomes because FBR provides no
   * merchant idempotency key and a second POST could issue a second invoice.
   */
  async retryFbr(orderId: number): Promise<void> {
    const res = await posClient.post(`/orders/${orderId}/fbr/retry`, undefined, {
      timeout: 45_000,
    });
    wireFbrRetrySchema.parse(res.data);
  },

  /**
   * PUT {pos}/orders/{id} — edit an order at any lifecycle stage (V2.3-01).
   *
   * Unlike updateStatus, this DOES return the authoritative order, so the
   * caller does not have to re-fetch to learn what it became. It still should
   * reconcile the board afterwards, because the edit also broadcasts and other
   * surfaces have to catch up.
   *
   * NO AbortSignal, deliberately, and for the same reason the create has none:
   * cancelling mid-flight manufactures ambiguity about whether the edit landed.
   * The request runs to completion or times out, and `client_edit_id` makes the
   * retry after a timeout resolve to the original edit rather than a second one.
   *
   * Three failures are raised as their own types because the operator's next
   * action differs for each. Everything else propagates for normalizeApiError.
   */
  async edit(
    orderId: number,
    payload: OrderEditPayload,
    correction = false,
  ): Promise<AppliedOrderEdit> {
    try {
      const res = await posClient.put(
        `/orders/${orderId}${correction ? '/correction' : ''}`,
        payload,
        {
          timeout: ORDER_EDIT_TIMEOUT_MS,
        },
      );
      return normalizeAppliedEdit(wireOrderEditResponseSchema.parse(res.data));
    } catch (e) {
      // Read the RAW body rather than the normalized error: normalizeApiError
      // flattens every envelope to a message, and these three are told apart by
      // `code` and carry state the editor needs (the fresh order, the missing
      // capability).
      if (isAxiosError(e) && (e.response?.status === 409 || e.response?.status === 403)) {
        const parsed = wireOrderEditConflictSchema.safeParse(e.response.data);
        const code = parsed.success ? parsed.data.code : null;
        const message = parsed.success ? parsed.data.error : null;

        if (e.response.status === 403) {
          const capability =
            parsed.success &&
            typeof (parsed.data as { capability?: unknown }).capability === 'string'
              ? (parsed.data as { capability: string }).capability
              : null;
          throw new OrderEditForbiddenError(
            capability,
            message ?? 'You do not have permission to save this edit.',
          );
        }

        if (code === 'IDEMPOTENCY_CONFLICT') {
          throw new OrderEditOperationConflictError(
            message ?? 'This edit id was already used for a different change.',
          );
        }

        throw new OrderEditConflictError(
          parsed.success && parsed.data.order ? normalizeOrder(parsed.data.order) : null,
          parsed.success ? (parsed.data.current_revision ?? null) : null,
          message ?? 'This order was changed by someone else while you were editing it.',
        );
      }

      throw e;
    }
  },
};
